import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {AbstractControl, UntypedFormControl, Validators} from '@angular/forms';
import {Subject, Subscription} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {UploadSelectorComponent} from './upload-selector.component';
import {UploadClient, UploadFinalizeResponse} from '@ee/common/services';
import {BytesPipe} from '@ee/common/pipes';
import {MAX_FILE_UPLOAD_SIZE} from '@ee/common/constants';

export type UploaderState =
  | 'idle'
  | 'uploading'
  | 'scanning'
  | 'ready'
  | 'infected'
  | 'rejected'
  | 'failed';

@Component({
  selector: 'ee-uploader',
  template: `
    <div [ngClass]="additionalClasses">
      @if (!!label) {
        <div class="mb-4 flex flex-row justify-between items-start">
          <h3 class="no-wrap">{{ label }}</h3>
          @if (form?.hasValidator(Validators.required)) { <span class="required-badge">Required</span>}
        </div>
      }
      <div class="card-content flex flex-col">
        <div class="flex flex-row justify-stretch items-stretch flex-1">
          @if (downloadFileLink) {
            <div class="flex flex-col flex-1 h-full w-full justify-start items-stretch mr-2">
              <div>1. Click here to download the form:</div>
              <button type="button" mat-flat-button color="primary" (click)="downloadForm(downloadFileLink)">
                Download Form
              </button>
              <div>2. Print and fill out the form</div>
              <div>3. Upload form here</div>
            </div>
          }
          <ee-upload-selector #uploadSelected class="flex-1"
            [formControl]="$any(form)" [required]="form.hasValidator(Validators.required)"
            [hasError]="$any(form).touched && form?.invalid" [accept]="accept"
            [uploadState]="uploadState" [progressPercent]="progressPercent"
            [statusReason]="rejectionReason">
          </ee-upload-selector>
        </div>

        <div class="below-dropzone mt-1">
          @if (!form?.untouched && form?.invalid && uploadState === 'idle') {
            <div class="field-error">
              @if (form?.hasError('required')) {
                Document must be selected to continue.
              } @else if (form?.hasError('maxContentSize')) {
                The total size must not exceed {{ form?.getError('maxContentSize').maxSize | bytes : 0 : 'B' : 'MB' }}.
              }
            </div>
          } @else if (!!hint) {
            <div class="uploader-hint">{{ hint }}</div>
          }
          @if (uploadState !== 'idle' || (form?.value?.length > 0)) {
            <div class="flex flex-row justify-end">
              <div class="no-wrap cursor-pointer clear-selection" (click)="clearSelectedFile(form)">
                <u>Clear Selection</u>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
    `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        min-width: 0;
      }

      :host > div {
        display: flex;
        flex-direction: column;
        flex: 1;
      }

      h3 {
        font-size: 1.1rem;
        color: var(--color-on-surface);
      }

      .required-badge {
        font-size: 0.7rem;
        font-weight: 600;
        color: var(--primary-red);
        background: rgba(var(--primary-red-rgb, 211, 47, 47), 0.08);
        border: 1px solid var(--primary-red);
        border-radius: 3px;
        padding: 0 6px;
        margin-left: 8px;
        vertical-align: middle;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .uploader-required {
        border-left: 3px solid var(--primary-red);
      }

      .mat-mdc-form-field {
        margin-bottom: 1rem;
      }

      .no-wrap {
        white-space: nowrap;
      }

      .card-content {
        min-width: 0;
        padding: 0 !important;
        overflow: hidden;
        flex: 1;

        > .flex {
          min-width: 0;
          overflow: hidden;
          flex: 1;
        }
      }

      .errors {
        color: var(--primary-red);
        text-align: right;
      }

      .field-error {
        font-size: 0.75rem;
        color: var(--primary-red);
        margin-top: 2px;
        line-height: 1.2;
        text-align: center;
      }

      .uploader-hint {
        font-size: 0.75rem;
        color: var(--color-on-surface-variant, rgba(0, 0, 0, 0.6));
        margin-top: 2px;
        line-height: 1.2;
      }

      .clear-selection {
        font-size: 0.75rem;
        color: var(--color-on-surface-variant, rgba(0, 0, 0, 0.6));
        line-height: 1.2;
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class UploaderComponent implements OnInit, OnDestroy {
  @Input({required: false}) label: string;
  @Input({required: false}) hint: string;
  @Input({required: false}) downloadFileLink: string;
  @Input({required: false}) accept: string = '*';
  @Input({required: false}) additionalClasses: string = '';
  /**
   * Hard cap on the selected file size, enforced in startUpload() regardless of
   * whether the bound control has a maxContentSize validator. Defaults to the
   * global 30 MiB limit so every uploader surface is protected even if its form
   * forgot to attach the validator. A control-level maxContentSize validator with
   * a stricter limit (e.g. 5 MiB templates) still takes precedence.
   */
  @Input({required: false}) maxSize: number = MAX_FILE_UPLOAD_SIZE;
  @Input() form: UntypedFormControl;
  @Input() attorney_org_id?: string;

  @Output() downloadFormEvent = new EventEmitter<string>();
  @Output() uploadReady = new EventEmitter<{ upload_id: string; original_format: string }>();
  @Output() uploadCleared = new EventEmitter<void>();

  @ViewChild('uploadSelected') uploadSelected: UploadSelectorComponent;

  public visibility: boolean[] = [];
  public required: boolean[] = [];

  uploadState: UploaderState = 'idle';
  progressPercent = 0;
  rejectionReason: string | null = null;

  private uploadId: string | null = null;
  private originalFormat: string | null = null;
  private abortController: AbortController | null = null;
  private pollingTimer: ReturnType<typeof setTimeout> | null = null;
  private pollingStartTime: number | null = null;
  private lastFile: File | null = null;
  private formValueSub: Subscription | null = null;
  private destroyed$ = new Subject<void>();
  private isDestroyed = false;

  private static readonly POLL_INITIAL_DELAY = 2000;
  private static readonly POLL_MULTIPLIER = 1.5;
  private static readonly POLL_MAX_DELAY = 10000;
  private static readonly POLL_WALL_CLOCK_LIMIT = 90000;
  /**
   * Delay before retrying `init` once when the first intake request hits a WAF silent-challenge
   * (a 2xx with no `upload_id`) before the edge token is verified. A second `init` succeeds.
   */
  private static readonly INIT_RETRY_DELAY = 750;

  constructor(
    private cdr: ChangeDetectorRef,
    private uploadClient: UploadClient
  ) {}

  ngOnInit(): void {
    if (this.form) {
      this.formValueSub = this.form.valueChanges.subscribe((value: File[] | null) => {
        if (value && value.length > 0 && value[0] instanceof File) {
          const file = value[0];
          // Only start upload if this is a new file selection
          if (file !== this.lastFile) {
            this.lastFile = file;
            this.startUpload(file);
          }
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.destroyed$.next();
    this.destroyed$.complete();
    this.formValueSub?.unsubscribe();
    this.stopPolling();
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  public downloadForm(formLink: string): void {
    this.downloadFormEvent.emit(formLink);
  }

  public clearSelectedFile(control: AbstractControl): void {
    // Abort in-flight upload
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    // Stop polling
    this.stopPolling();

    // Best-effort delete staged object
    if (this.uploadId) {
      this.uploadClient.delete(this.uploadId).pipe(takeUntil(this.destroyed$)).subscribe({
        error: () => { /* best-effort, ignore */ }
      });
    }

    // Reset state
    this.uploadState = 'idle';
    this.progressPercent = 0;
    this.rejectionReason = null;
    this.uploadId = null;
    this.originalFormat = null;
    this.lastFile = null;

    // Clear the form control and selector
    control.setValue(undefined);
    this.uploadSelected?.clear();

    this.uploadCleared.emit();
    this.cdr.detectChanges();
  }

  public retryUpload(): void {
    if (this.lastFile) {
      this.startUpload(this.lastFile);
    }
  }

  /** Resets the upload state without clearing the form control (parent handles that). */
  public resetUploadState(): void {
    this.stopPolling();
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.uploadState = 'idle';
    this.progressPercent = 0;
    this.rejectionReason = null;
    this.uploadId = null;
    this.originalFormat = null;
    this.lastFile = null;
    this.cdr.detectChanges();
  }

  getStatusClass(): string {
    switch (this.uploadState) {
      case 'uploading': return 'state-uploading';
      case 'scanning': return 'state-scanning';
      case 'ready': return 'state-ready';
      case 'infected': return 'state-infected';
      case 'rejected': return 'state-rejected';
      case 'failed': return 'state-failed';
      default: return '';
    }
  }

  private startUpload(file: File, attempt = 0): void {
    // Reset previous state
    this.stopPolling();
    if (this.abortController) {
      this.abortController.abort();
    }

    // Always notify the parent that any previously emitted upload_id is now invalid.
    // This covers: ready → new file selected (parent was holding an upload_id),
    // uploading/scanning → new file selected (parent may have a stale upload_id from
    // an even earlier upload), and the init-in-flight race where uploadId is null but
    // uploadState is already 'uploading'.
    this.uploadCleared.emit();

    // Reject oversized files before uploading instead of silently accepting them.
    // Prefer the control's maxContentSize validator verdict when present — it carries
    // the correct per-control limit (e.g. 5 MiB for document templates). Otherwise fall
    // back to this component's maxSize input (default 30 MiB) so upload surfaces that
    // never wired the validator still enforce a cap. Without this an oversized file
    // uploaded and the tile showed "Ready" while Save silently did nothing.
    const validatorMax: number | null = this.form?.hasError('maxContentSize')
      ? this.form.getError('maxContentSize').maxSize
      : null;
    const effectiveMax = validatorMax ?? this.maxSize;
    if (validatorMax !== null || file.size > effectiveMax) {
      // When the rejection comes from the component-level hard cap (no maxContentSize
      // validator error present), mirror it onto the control so parent forms that gate
      // submission on control validity are blocked too — not just the visual tile.
      if (validatorMax === null && this.form) {
        // Merge with existing errors so unrelated validation state (required, fileExtension,
        // custom) isn't discarded by this hard-cap rejection.
        this.form.setErrors({...this.form.errors, maxContentSize: {actualSize: file.size, maxSize: effectiveMax}});
      }
      this.uploadState = 'rejected';
      this.rejectionReason =
        `File exceeds the ${BytesPipe.transform(effectiveMax, 0, 'B', 'MB')} limit. Please select a smaller file.`;
      this.progressPercent = 0;
      this.uploadId = null;
      this.originalFormat = null;
      this.cdr.detectChanges();
      return;
    }

    // File is within the limit — clear any hard-cap error left over from a previous
    // oversized selection so the control's validity recovers.
    if (this.form?.hasError('maxContentSize')) {
      const remaining = {...this.form.errors};
      delete remaining['maxContentSize'];
      this.form.setErrors(Object.keys(remaining).length ? remaining : null);
    }

    this.uploadState = 'uploading';
    this.progressPercent = 0;
    this.rejectionReason = null;
    this.uploadId = null;
    this.originalFormat = null;
    this.cdr.detectChanges();

    // Anonymous intake uploads set attorney_org_id; the WAF token is attached centrally by
    // WafTokenInterceptor (see .kiro/specs/waf-captcha-intake), not passed per-call here.
    const opts: { attorney_org_id?: string } = {};
    if (this.attorney_org_id) {
      opts.attorney_org_id = this.attorney_org_id;
    }

    this.uploadClient.init(file, opts).pipe(takeUntil(this.destroyed$)).subscribe({
      next: (response) => {
        // Ignore a response for a file that's no longer the active selection: the user cleared it or
        // picked a different file while init was in flight (lastFile is nulled/changed). Acting on it
        // — or scheduling a retry for it — would clobber or abort the newer upload.
        if (this.isDestroyed || this.lastFile !== file) {
          return;
        }
        // A WAF silent-challenge can return a 2xx with an empty body (no upload_id) on the first
        // intake request after page load, before the edge token is verified. Reading .upload_id off
        // it would throw and leave the tile stuck on 'uploading'. Retry init once — a second init
        // succeeds once the token settles (this is what a manual re-select does today).
        if (!response?.upload_id) {
          if (attempt < 1) {
            setTimeout(() => {
              // Re-check on fire: only retry if this file is still the active selection.
              if (!this.isDestroyed && this.lastFile === file) {
                this.startUpload(file, attempt + 1);
              }
            }, UploaderComponent.INIT_RETRY_DELAY);
          } else {
            this.uploadState = 'failed';
            this.cdr.detectChanges();
          }
          return;
        }
        this.uploadId = response.upload_id;
        this.abortController = new AbortController();

        const progress$ = new Subject<number>();
        progress$.pipe(takeUntil(this.destroyed$)).subscribe(percent => {
          this.progressPercent = percent;
          this.cdr.detectChanges();
        });

        this.uploadClient.put(
          response.presigned_url,
          file,
          this.abortController,
          progress$
        ).pipe(takeUntil(this.destroyed$)).subscribe({
          next: () => {
            this.uploadState = 'scanning';
            this.cdr.detectChanges();
            this.startPolling();
          },
          error: () => {
            // Don't set failed if it was an intentional abort (clear)
            if (this.uploadState === 'uploading') {
              this.uploadState = 'failed';
              this.cdr.detectChanges();
            }
          }
        });
      },
      error: (err) => {
        // Same stale-selection guard as the next handler: a late error for a file
        // the user already cleared or replaced must not clobber the newer state.
        if (this.isDestroyed || this.lastFile !== file) {
          return;
        }
        // Surface the server's rejection reason (problem-detail title, e.g.
        // "File type .msg is not supported") instead of a bare failed state.
        // Fall back to message/detail (other backend error shapes) or a plain string
        // body. Deliberately NOT err.message: Angular synthesizes it from the URL and
        // status ("Http failure response for …"), which is noise in the tile.
        this.rejectionReason = typeof err?.error === 'string'
          ? (err.error || null)
          : err?.error?.title ?? err?.error?.message ?? err?.error?.detail ?? null;
        this.uploadState = 'failed';
        this.cdr.detectChanges();
      }
    });
  }

  private startPolling(): void {
    this.pollingStartTime = Date.now();
    this.pollFinalize(UploaderComponent.POLL_INITIAL_DELAY);
  }

  private pollFinalize(delay: number): void {
    this.pollingTimer = setTimeout(() => {
      if (this.isDestroyed || this.uploadState !== 'scanning' || !this.uploadId) return;

      // Check wall-clock limit
      const elapsed = Date.now() - (this.pollingStartTime ?? Date.now());
      if (elapsed >= UploaderComponent.POLL_WALL_CLOCK_LIMIT) {
        this.uploadState = 'rejected';
        this.rejectionReason = 'Scan timed out, please try again';
        this.cdr.detectChanges();

        // Best-effort delete
        if (this.uploadId) {
          this.uploadClient.delete(this.uploadId).pipe(takeUntil(this.destroyed$)).subscribe({
            error: () => { /* best-effort */ }
          });
        }
        return;
      }

      const opts: { attorney_org_id?: string } = {};
      if (this.attorney_org_id) {
        opts.attorney_org_id = this.attorney_org_id;
      }

      this.uploadClient.finalize(this.uploadId, opts).pipe(takeUntil(this.destroyed$)).subscribe({
        next: (response: UploadFinalizeResponse) => {
          if (this.isDestroyed) return;
          switch (response.status) {
            case 'ready':
              this.uploadState = 'ready';
              this.originalFormat = response.original_format ?? null;
              this.cdr.detectChanges();
              this.uploadReady.emit({
                upload_id: response.upload_id,
                original_format: response.original_format ?? ''
              });
              break;
            case 'scanning': {
              // Schedule next poll with exponential backoff
              const nextDelay = Math.min(
                delay * UploaderComponent.POLL_MULTIPLIER,
                UploaderComponent.POLL_MAX_DELAY
              );
              this.pollFinalize(nextDelay);
              break;
            }
            case 'infected':
              this.uploadState = 'infected';
              this.cdr.detectChanges();
              break;
            case 'rejected':
              this.uploadState = 'rejected';
              this.rejectionReason = response.reason ?? 'File rejected';
              this.cdr.detectChanges();
              break;
            default:
              this.uploadState = 'failed';
              this.cdr.detectChanges();
              break;
          }
        },
        error: () => {
          if (this.isDestroyed) return;
          this.uploadState = 'failed';
          this.cdr.detectChanges();
        }
      });
    }, delay);
  }

  private stopPolling(): void {
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
    this.pollingStartTime = null;
  }

  protected readonly Validators = Validators;
}
